// ---------------------------------------------------------------------------
// 保存系 API の失敗を、画面に出せる Error にそろえる
// ---------------------------------------------------------------------------
// 後端（cms saver → BFF）は失敗時に {"status": "...", "error": {"<コード>": "<英語メッセージ>"}}
// を返す。以前は error がオブジェクトのまま new Error() に渡されて
// "[object Object]" になり、status / error も失われていた。
//
// 投げる Error の形:
//   message … 画面に出せる日本語（コード別の文言 → error の値 → 既定文）
//   status  … 後端の status（OK 以外）。タイムアウトでは undefined
//   error   … 後端の error（{コード: メッセージ} の元のまま）
//   code    … error の最初のコード。無ければ undefined。タイムアウトは 'TIMEOUT'

export const ERROR_CODE_LOCK_TIMEOUT = 'E000011';
export const ERROR_CODE_TIMEOUT = 'TIMEOUT';

export const ERROR_MESSAGES = {
  // ロックを 20 秒待って取れなかった。この時点では何も書き込まれていない。
  E000011: '他の処理（同時に行われた保存など）が終わるのを待ちましたが、時間内に終わりませんでした。保存はされていません。少し待ってからもう一度お試しください。',
  // 投稿の並び順リセット中
  U000066: '並び順のリセット中のため、投稿の追加・並び替え・削除はできません。リセットが終わってからもう一度お試しください。',
  // 投稿タイプのデータ取込中
  U000144: 'この投稿タイプはデータ取込中のため、投稿の追加・並び替えはできません。取込が終わってからもう一度お試しください。',
  // 投稿タイプのテキスト一括置換中
  U000145: 'この投稿タイプはテキストの一括置換中のため、投稿の追加・並び替えはできません。置換が終わってからもう一度お試しください。',
  // 証明書の自動発行中（ドメインロックを持っている）
  U000146: '証明書の発行中のため、このドメインの証明書は操作できません。しばらく待ってからもう一度お試しください。',
  // 二段階認証・ログイン (後端のメッセージは英語。日本語はここでコードから引く)
  U000097: '「メールアドレス」か「パスワード」が間違っています。',
  U000119: '二段階認証がセットアップされていません。',
  U000120: '認証コードが正しくありません。',
  U000121: '二段階認証が有効化されていません。',
  U000122: '二段階認証は既に有効化されています。',
  U000137: '認証コードの失敗が続いたため、しばらく認証できません。時間をおいて再度お試しください。',
  U000147: 'ログインの失敗が続いたため、しばらくログインできません。時間をおいて再度お試しください。',
  // 入力チェック
  U000143: 'private_sys_shopify_cms_url は https の URL にしてください。',
  U000142: 'フォルダ名に / \\ は使えません（. .. や空白だけの名前も不可）。',
  U000123: 'パラメータが正しくありません。',
  // 開いたあとに他の人が保存した（保存の衝突）。画面ごとの専用文言がある所はそちらが優先される。
  U000132: '開いたあとに他の人が保存しました。最新の内容を読み込んでからもう一度保存してください。',
  // BFF の 30 秒タイムアウト。後端が実行済みの可能性がある（結果は不明）。
  [ERROR_CODE_TIMEOUT]: 'サーバーの応答が時間内にありませんでした。保存・変更の操作だった場合は、反映されている可能性があります。結果を確認してから、必要ならもう一度操作してください。',
};

// 「フィールドの入力エラー」ではなく、画面全体のメッセージとして出すべき USER_ERROR のコード。
export const NON_FIELD_USER_ERROR_CODES = ['U000066', 'U000144', 'U000145', 'U000146'];

// 保存画面の catch 用: 項目ごとの入力エラー（setErrorList で並べるもの）か。
// USER_ERROR でも U000066 等はフィールドのエラーではないので false。
export const isFieldUserError = (error) =>
  error?.status === 'USER_ERROR' && !NON_FIELD_USER_ERROR_CODES.includes(error?.code);

const DEFAULT_MESSAGE = 'エラーが発生しました。';

const valueToText = (v) => {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '';
  try {
    return JSON.stringify(v);
  } catch (e) {
    return String(v);
  }
};

// error（{コード: メッセージ} / 文字列）の最初のコード。無ければ undefined。
export function getErrorCode(error) {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return undefined;
  const keys = Object.keys(error);
  return keys.length > 0 ? keys[0] : undefined;
}

// 画面に出す日本語メッセージ。"[object Object]" にはしない。
export function buildErrorMessage(error) {
  const code = getErrorCode(error);
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];

  if (typeof error === 'string') return error || DEFAULT_MESSAGE;

  if (error && typeof error === 'object') {
    const values = (Array.isArray(error) ? error : Object.values(error))
      .map(valueToText)
      .filter(Boolean);
    if (values.length > 0) return values.join('、');
  }
  return DEFAULT_MESSAGE;
}

// 後端の本文（{status, error}）から Error を作る。
export function createApiError(status, error) {
  const apiError = new Error(buildErrorMessage(error));
  apiError.status = status;
  apiError.error = error;
  apiError.code = getErrorCode(error);
  return apiError;
}

// BFF のタイムアウト（HTTP 408 / 504）。後端が実行済みかもしれないので再試行しない。
export function createTimeoutError(httpStatus) {
  const apiError = new Error(ERROR_MESSAGES[ERROR_CODE_TIMEOUT]);
  apiError.status = undefined;
  apiError.error = undefined;
  apiError.code = ERROR_CODE_TIMEOUT;
  apiError.httpStatus = httpStatus;
  return apiError;
}

export const isTimeoutHttpStatus = (httpStatus) => httpStatus === 408 || httpStatus === 504;

// E000011 の自動再送の待ち時間。テストで差し替えられるようオブジェクトにしてある。
export const saveRetryConfig = {
  lockTimeoutDelayMs: 2000,
};

// ---------------------------------------------------------------------------
// cms_ai の「このサイトの権限なし」
// ---------------------------------------------------------------------------
// cms_ai はサイトの閲覧権限が無いユーザーを HTTP 403 で弾く。本文は
// {"status":"PRAMETA_ERROR","error":{"error":"No permission for this site"}} で、
// createApiError を通ると error.error.error / message にこの英文が残る。
export const SITE_PERMISSION_DENIED_DETAIL = 'No permission for this site';
export const SITE_PERMISSION_DENIED_MESSAGE = 'このサイトで AI 機能を使う権限がありません。';

export const isSitePermissionDenied = (err) =>
  err?.error?.error === SITE_PERMISSION_DENIED_DETAIL || err?.message === SITE_PERMISSION_DENIED_DETAIL;

// cms_ai は旧チャットの WS を、"Invalid token"（chat token が無い／閉じている）と
// "Forbidden"（サイト権限なし）のどちらでも 4001 で閉じる。reason で見分けること。
export const WS_CLOSE_INVALID_OR_FORBIDDEN = 4001;
